import { computed, ComputedRef, reactive, toRefs, watch } from 'vue';
import { LocationQuery } from 'vue-router';
import { useApiAuth, useToast } from '@/hooks';
import {
  IClaimStatus,
  IClaim,
  IClaimInventoryItem,
  IClaimNote,
  IClaimList,
  IClaimNoteOrReply,
  IClaimMedia,
} from '@/types';
import { CLAIM_STATUS, CLAIM_STATUS_LABEL_MAP, PENDING_STATUSES } from '@/data';
import { generateClaimQuery } from '@/utils';
import { cloneDeep, isEmpty, isEqual, pick } from 'lodash';

interface IClaimState {
  pages: number | undefined;
  total: number | undefined;
  claims: IClaim[];
  statuses: IClaimStatus[];
  loading: boolean;
  error: string | null;
}

const claimsState = reactive<IClaimState>({
  pages: undefined,
  total: undefined,
  claims: [],
  statuses: [],
  loading: false,
  error: null,
});

export const useClaims = (claimId?: number, batchCode?: string) => {
  const { toggleToast } = useToast();

  /**
   * I am the front-end half of ClaimController.getAllStatusTotal
   */
  const { data: claimsStatusData, get: getClaimsStatus } = useApiAuth<IClaimStatus[]>(
    `/claim/status`
  );

  function extendClaimNotes(
    source: ComputedRef<IClaimList | undefined>
  ): ComputedRef<IClaimList | undefined> {
    return computed(() => {
      const sourceData = source.value;
      sourceData?.claims.forEach((claim: IClaim) => {
        claim.notes?.forEach((note: IClaimNote) => {
          note.noteType = 'note';

          note.replies?.forEach((reply: IClaimNoteOrReply) => {
            reply.noteType = 'reply';
          });
        });
      });
      return sourceData;
    });
  }

  /**
   * I am the front-end half of ClaimController.getClaims
   */
  const { loading: loadingClaims, data: _claimsDataRaw, error: claimsError, get } = useApiAuth<
    IClaimList
  >(`/claim`);

  const claimsData = extendClaimNotes(_claimsDataRaw);

  /**
   * On the back-end, `/claim/batch/{batchCode}` and `/claim/batch` are the same endpoint.
   * So we want `getBatchClaims` to be the same method we call regardless of whether
   * we actually know the `batchCode`.  But we can't have `undefined` showing up in that URL…
   */
  const _batchEndpoint: string = batchCode ? `/claim/batch/${batchCode}` : '/claim/batch';

  /**
   * I am the front-end half of ClaimController.getBatchClaims
   */
  const {
    loading: loadingBatchClaims,
    data: _batchClaimsDataRaw,
    error: batchClaimsError,
    get: getBatchClaims,
  } = useApiAuth<IClaimList>(_batchEndpoint);

  const batchClaimsData = extendClaimNotes(_batchClaimsDataRaw);

  /**
   * I am the front-end half of ClaimController.updateClaim
   */
  const { success: updateClaimSuccess, patch: updateClaim } = useApiAuth<void>(`/claim/${claimId}`);

  const updateClaimLocal = (payload: IClaim) => {
    if (!payload.id) return;

    const currentClaimIndex: number = claimsState.claims.findIndex(
      (claim: IClaim) => claim.id === payload.id
    );
    const originClaim = cloneDeep(claimsState.claims[currentClaimIndex]);
    const updatedClaim: IClaim = { ...originClaim, ...payload };

    // if the status changes and is not settled, remove the claim from the current local claims list, otherwise update data
    if (payload.status && originClaim.status && payload.status !== originClaim.status) {
      if (payload.status !== CLAIM_STATUS.QA_SETTLED) {
        claimsState.claims.splice(currentClaimIndex, 1);
      }

      toggleToast(
        true,
        'success',
        `Successfully updated Claim ${payload.id} from ${
          CLAIM_STATUS_LABEL_MAP[originClaim.status]
        } to ${CLAIM_STATUS_LABEL_MAP[payload.status]}`
      );
    } else {
      let toggledToast = false;
      claimsState.claims[currentClaimIndex] = cloneDeep(updatedClaim);

      if (updatedClaim.media && originClaim.media) {
        const updatedMedia = updatedClaim.media.map(function(media: IClaimMedia) {
          return pick(media, 'id', 'isPrivate');
        });

        const originalMedia = originClaim.media.map(function(media: IClaimMedia) {
          return pick(media, 'id', 'isPrivate');
        });

        if (!isEqual(updatedMedia, originalMedia)) {
          toggledToast = true;
          toggleToast(true, 'success', 'Claim media successfully updated.');
        }
      }

      if (!toggledToast && !isEqual(updatedClaim, originClaim)) {
        toggleToast(true, 'success', 'Claim successfully updated.');
      }
    }
  };

  const updateClaimNotesLocal = (value: IClaimNoteOrReply, claimIdToUpdate: number) => {
    if (!claimIdToUpdate) return;

    const currentClaimIndex: number = claimsState.claims.findIndex(
      (claim: IClaim) => claim.id === claimIdToUpdate
    );
    const currentClaim = claimsState.claims[currentClaimIndex];

    if (currentClaimIndex === -1) return;

    if (value.noteType === 'reply') {
      const parentNote = currentClaim.notes?.find(
        (note: IClaimNote) => value.parent_id && note.id === value.parent_id
      );

      if (!parentNote) {
        throw new Error("Can't add a reply to a note that doesn't exist");
      }

      const currentReply = parentNote.replies?.find(
        (reply: IClaimNoteOrReply) => value.id && value.id === reply.id
      );

      if (currentReply) {
        currentReply.text = value.text;
      } else {
        parentNote.replies?.push(value);
      }
    } else if (value.noteType === 'note') {
      // check if notes exist or not, if true, the action is update, otherwise the action is add
      if (currentClaim.notes && currentClaim.notes.length > 0) {
        const currentNote = currentClaim.notes?.find((note: IClaimNote) => note.id === value.id);

        if (!currentNote) return;
        currentNote.text = value.text;
      } else {
        currentClaim.notes = [value];
      }
    }
  };

  const getClaimsStatusByFilter = (filter: LocationQuery) => {
    const query = { ...filter };

    delete query.status;
    delete query.page;
    delete query.size;

    const statusQuery = generateClaimQuery(query);

    if (isEmpty(statusQuery)) {
      getClaimsStatus();
    } else {
      getClaimsStatus(statusQuery);
    }
  };

  const getClaims = (filter: LocationQuery) => {
    // reset current claims data and error
    claimsState.claims = [];
    claimsState.error = null;

    const query = { ...filter };

    if (PENDING_STATUSES.some(arr => arr === query.status)) {
      if (query.status === CLAIM_STATUS.QA_PENDING_ALL) {
        delete query.status;
      }
      getBatchClaims(generateClaimQuery(query));
    } else {
      get(generateClaimQuery(query));
    }
  };

  const getStatusTotal = (status: string): number => {
    const total: number | undefined = claimsState.statuses.find(
      (data: IClaimStatus) => data.status === status
    )?.total;

    return total || 0;
  };

  const getSupportRequestsNum = (): number => getStatusTotal(CLAIM_STATUS.CUSTOMER_CARE_OPEN);

  const getNewTicketsNum = (): number => getStatusTotal(CLAIM_STATUS.CUSTOMER_CARE_RESOLVED);

  const getOpenClaimsNum = (): number =>
    getStatusTotal(CLAIM_STATUS.QA_OPEN) + getStatusTotal(CLAIM_STATUS.QA_COMPLETE);

  const getPendingClaimsNum = (): number =>
    getStatusTotal(CLAIM_STATUS.QA_SUBMITTED) +
    getStatusTotal(CLAIM_STATUS.QA_PENDING) +
    getStatusTotal(CLAIM_STATUS.QA_SETTLED);

  const getArchivedClaimsNum = (): number => getStatusTotal(CLAIM_STATUS.QA_CLOSED);

  const getProductBoxLotNo = (claim: IClaim, productBoxId: number): number | string => {
    const inventoryItem = claim.orderItem?.inventoryItems.find(
      (inventoryItem: IClaimInventoryItem) => inventoryItem.productBoxId === productBoxId
    );

    return inventoryItem ? inventoryItem.outlayItem.inventory.lotNo : '';
  };

  // watch value changes and assign to corresponding property in claimState
  watch([loadingClaims, claimsData, claimsError], () => {
    claimsState.pages = claimsData.value?.pages;
    claimsState.total = claimsData.value?.total;
    claimsState.claims = claimsData.value?.claims || [];
    // Note: the loading, error value can't be directly used in a component, the reason is because the claimsState is lifted and shared in multiple places (so no instance duplicates), thus will also need to map the loading, error value into the claimsState
    claimsState.loading = loadingClaims.value;
    claimsState.error = claimsError.value;
  });
  watch([loadingBatchClaims, batchClaimsData, batchClaimsError], () => {
    claimsState.pages = batchClaimsData.value?.pages;
    claimsState.total = batchClaimsData.value?.total;
    claimsState.claims = batchClaimsData.value?.claims || [];
    claimsState.loading = loadingBatchClaims.value;
    claimsState.error = batchClaimsError.value;
  });

  watch(claimsStatusData, () => {
    claimsState.statuses = claimsStatusData.value || [];
  });

  return {
    ...toRefs(claimsState),
    getClaims,
    getClaimsStatusByFilter,
    getClaimsStatus,
    getSupportRequestsNum,
    getNewTicketsNum,
    getStatusTotal,
    getOpenClaimsNum,
    getPendingClaimsNum,
    getArchivedClaimsNum,
    updateClaim,
    updateClaimSuccess,
    updateClaimLocal,
    updateClaimNotesLocal,
    getProductBoxLotNo,
  };
};
